import { useEffect, useCallback, useState } from 'react';
import { useSocket } from './useSocket';
import { useFriendsStore } from '@/store/friendsStore';
import type { Friend, FriendRequest } from '@/types';

interface Party {
  id: string;
  leaderId: number;
  members: Array<{ id: number; username: string; avatar: string }>;
  messages: Array<{ username: string; message: string; timestamp: Date }>;
  isLeader: boolean;
}

interface OpenParty {
  id: string;
  leaderId: number;
  leaderUsername: string;
  leaderAvatar: string;
  gameId: number | null;
  status: string;
  memberCount: number;
  maxMembers: number;
  members: Array<{ id: number; username: string; avatar: string }>;
  createdAt: string;
}

export function useFriends() {
  const socket = useSocket('friends');
  const {
    friends,
    friendRequests,
    onlineFriends,
    setFriends,
    setFriendRequests,
    addFriend,
    removeFriend: removeFromStore,
    updateFriendStatus,
    addFriendRequest,
    removeFriendRequest: removeRequestFromStore,
  } = useFriendsStore();

  const [party, setParty] = useState<Party | null>(null);
  const [openParties, setOpenParties] = useState<OpenParty[]>([]);
  // Track unread DMs per friend: { friendUserId: count }
  const [unreadMessages, setUnreadMessages] = useState<Record<string, number>>({});
  // Callbacks for real-time DM updates (DirectMessageModal subscribes to this)
  const [dmListeners] = useState<Set<(msg: any) => void>>(() => new Set());
  const isConnected = socket?.connected ?? false;

  // Expose partyId globally so matchmaking pages can access it
  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).__currentPartyId = party?.id || null;
    }
    return () => { if (typeof window !== 'undefined') { (window as any).__currentPartyId = null; } };
  }, [party?.id]);

  // Initialize friends data on connect
  useEffect(() => {
    if (!socket) return;

    const handleInitialData = (data: {
      friends: Friend[];
      pendingRequests: any[];
    }) => {
      console.log('[useFriends] Received initial data:', data);
      setFriends(data.friends);

      // Map friend requests to ensure consistent format
      const mappedRequests = (data.pendingRequests || []).map(req => ({
        id: req.id || req.request_id,
        from_user_id: req.from_user_id,
        from_username: req.from_username || req.username,
        from_avatar: req.from_avatar || req.avatar_url || `https://ui-avatars.com/api/?name=${req.from_username || req.username || 'User'}&background=random`,
        message: req.message,
        created_at: req.created_at
      }));

      setFriendRequests(mappedRequests);
    };

    // Request friend list when socket connects
    const handleConnect = () => {
      console.log('[useFriends] Socket connected, requesting friend list, party list, and own party');
      socket.emit('friend:list');
      socket.emit('party:list');
      socket.emit('party:my_party'); // Restore own party on reconnect
    };

    // If already connected, request immediately (only once on mount)
    let hasRequestedInitial = false;
    if (socket.connected && !hasRequestedInitial) {
      console.log('[useFriends] Already connected, requesting friend list, party list, and own party now');
      socket.emit('friend:list');
      socket.emit('party:list');
      socket.emit('party:my_party'); // Restore own party on reconnect
      hasRequestedInitial = true;
    }

    // Listen for connection event
    socket.on('connect', handleConnect);

    const handleFriendRequestReceived = (data: any) => {
      console.log('[useFriends] Friend request received:', data);
      addFriendRequest({
        id: data.id || data.requestId,
        from_user_id: data.from_user_id || data.fromUserId,
        from_username: data.from_username || data.fromUsername,
        from_avatar: data.from_avatar || data.fromAvatar || `https://ui-avatars.com/api/?name=${data.from_username || data.fromUsername || 'User'}&background=random`,
        message: data.message,
        created_at: data.created_at || new Date().toISOString(),
      });

      // Show toast notification
      if (typeof window !== 'undefined' && (window as any).toast) {
        (window as any).toast.success(`Friend request from ${data.from_username || data.fromUsername}`);
      }
    };

    const handleFriendAccepted = (data: {
      id?: number;
      userId?: number;
      username: string;
      avatar: string;
      is_online?: boolean;
      online?: boolean;
      status?: string;
    }) => {
      const friendId = data.id || data.userId;
      if (!friendId) return;

      addFriend({
        id: friendId,
        username: data.username,
        avatar: data.avatar,
        is_online: data.is_online ?? data.online ?? true,
      });
    };

    const handleFriendRemoved = (data: { id?: number; userId?: number }) => {
      const friendId = data.id || data.userId;
      if (!friendId) return;
      removeFromStore(friendId);
    };

    const handlePresenceUpdate = (data: {
      id?: number;
      userId?: number;
      status?: 'online' | 'offline';
      is_online?: boolean;
      online?: boolean;
    }) => {
      const friendId = data.id || data.userId;
      if (!friendId) return;

      // Support both formats for backward compatibility
      const isOnline = data.is_online !== undefined
        ? data.is_online
        : data.online !== undefined
        ? data.online
        : data.status === 'online';
      console.log('[useFriends] Presence update:', { friendId, isOnline, data });
      updateFriendStatus(friendId, isOnline);
    };

    // Party events
    // Helper to get userId from socket auth
    const getMyUserId = (): number | null => {
      try {
        const auth = (socket as any).auth;
        return auth?.userId || auth?.user_id || null;
      } catch { return null; }
    };

    const handlePartyCreated = (data: { party: any }) => {
      console.log('[useFriends] Party created:', data);
      const myId = getMyUserId();
      setParty({
        id: data.party.id,
        leaderId: data.party.leaderId,
        members: data.party.members || [],
        messages: [],
        isLeader: true,
      });
      // Refresh party list to show the new party
      socket.emit('party:list');
    };

    const handlePartyJoined = (data: { party: any }) => {
      console.log('[useFriends] Joined party:', data);
      const myId = getMyUserId();
      setParty({
        id: data.party.id,
        leaderId: data.party.leaderId,
        members: data.party.members || [],
        messages: [],
        isLeader: myId != null && data.party.leaderId === myId,
      });

      // Show success notification
      if (typeof window !== 'undefined' && (window as any).toast) {
        (window as any).toast.success('Joined party successfully!');
      }
    };

    // Restore own party on reconnect
    const handleMyPartyResponse = (data: { party: any | null }) => {
      console.log('[useFriends] My party response:', data);
      if (data.party) {
        const myId = getMyUserId();
        setParty({
          id: data.party.id,
          leaderId: data.party.leaderId,
          members: data.party.members || [],
          messages: [],
          isLeader: myId != null && data.party.leaderId === myId,
        });
      }
    };

    const handlePartyLeft = () => {
      console.log('[useFriends] Left party');
      setParty(null);
      // Refresh party list
      socket.emit('party:list');

      // Show notification
      if (typeof window !== 'undefined' && (window as any).toast) {
        (window as any).toast.info('Left party');
      }
    };

    const handlePartyInvitation = (data: { partyId: string; from: number; leader: string }) => {
      console.log('[useFriends] Party invitation received:', data);

      // Show immediate toast notification
      if (typeof window !== 'undefined' && (window as any).toast) {
        (window as any).toast.success(`Party invitation from ${data.leader}!`);
      }
    };

    // FIX: Use functional setParty to avoid stale closure over `party`
    const handlePartyMemberJoined = (partyData: { partyId: string; userId: number; party?: any }) => {
      console.log('[useFriends] Party member joined:', partyData);
      setParty((prev) => {
        if (!prev || prev.id !== partyData.partyId) return prev;
        return {
          ...prev,
          members: partyData.party?.members || prev.members,
        };
      });
    };

    // FIX: Use functional setParty to avoid stale closure over `party`
    const handlePartyMemberLeft = (partyData: { partyId: string; userId: number }) => {
      console.log('[useFriends] Party member left:', partyData);
      setParty((prev) => {
        if (!prev || prev.id !== partyData.partyId) return prev;
        return {
          ...prev,
          members: prev.members.filter((m) => m.id !== partyData.userId),
        };
      });
    };

    // FIX: Use sender username from server instead of hardcoded "Friend"
    const handlePartyChatMessage = (data: {
      from: number;
      fromUsername?: string;
      message: string;
      timestamp: Date;
    }) => {
      setParty((prev) =>
        prev
          ? {
              ...prev,
              messages: [
                ...prev.messages,
                {
                  username: data.fromUsername || prev.members.find(m => m.id === data.from)?.username || 'Unknown',
                  message: data.message,
                  timestamp: data.timestamp,
                },
              ],
            }
          : null
      );
    };

    const handlePartyListResponse = (data: { parties: OpenParty[] }) => {
      console.log('[useFriends] Party list received:', data);
      setOpenParties(data.parties || []);
    };

    const handlePartyListUpdated = () => {
      console.log('[useFriends] Party list updated, refreshing...');
      socket.emit('party:list');
    };

    const handlePartyGameStarted = (data: {
      gameId: number; gameName: string; mode?: string; partyId?: string;
      queuedMembers?: number; totalMembers?: number; errors?: any[];
    }) => {
      console.log('[useFriends] Party queued for game:', data);

      if (typeof window !== 'undefined') {
        const toastFn = (window as any).toast;
        if (data.errors && data.errors.length > 0) {
          toastFn?.error?.(`${data.queuedMembers}/${data.totalMembers} in Queue for ${data.gameName}`);
        } else {
          toastFn?.success?.(`Party in Queue for ${data.gameName}!`);
        }

        // Navigate to matchmaking page so user sees queue status
        const navigate = (window as any).__reactNavigate;
        if (navigate) {
          navigate('/matchmaking');
        }
      }
    };

    const handlePartyKicked = () => {
      console.log('[useFriends] You were kicked from the party');
      setParty(null);

      if (typeof window !== 'undefined' && (window as any).toast) {
        (window as any).toast.error('You were kicked from the party');
      }
    };

    // FIX: Handle leadership transfer notification
    const handlePartyLeaderChanged = (data: { partyId: string; newLeaderId: number; newLeaderUsername: string }) => {
      console.log('[useFriends] Party leader changed:', data);
      const myId = getMyUserId();
      setParty((prev) => {
        if (!prev || prev.id !== data.partyId) return prev;
        return {
          ...prev,
          leaderId: data.newLeaderId,
          isLeader: myId != null && data.newLeaderId === myId,
        };
      });

      if (typeof window !== 'undefined' && (window as any).toast) {
        (window as any).toast.info(`${data.newLeaderUsername} is now party leader`);
      }
    };

    // FIX: Listen for party errors
    const handlePartyError = (data: { error: string }) => {
      console.error('[useFriends] Party error:', data.error);
      if (typeof window !== 'undefined' && (window as any).toast) {
        (window as any).toast.error(data.error);
      }
    };

    // Listen for friend list response (from friend:list request)
    const handleFriendListResponse = (data: {
      friends: Friend[];
      onlineCount?: number;
      totalCount?: number;
    }) => {
      console.log('[useFriends] Received friend list response:', data);
      setFriends(data.friends);
    };

    // === REAL-TIME DM HANDLER ===
    const handleDmReceived = (msg: any) => {
      console.log('[useFriends] DM received:', msg);
      // Increment unread counter for this sender
      const senderId = String(msg.from_user_id);
      setUnreadMessages(prev => ({
        ...prev,
        [senderId]: (prev[senderId] || 0) + 1
      }));
      // Notify all DM modal listeners (so open chat windows update in real time)
      dmListeners.forEach(cb => cb(msg));
    };

    socket.on('connect', handleConnect);
    socket.on('friend:initial_data', handleInitialData);
    socket.on('friend:list_response', handleFriendListResponse);
    socket.on('friend:request_received', handleFriendRequestReceived);
    socket.on('friend:accepted', handleFriendAccepted);
    socket.on('friend:removed', handleFriendRemoved);
    socket.on('presence:update', handlePresenceUpdate);
    socket.on('dm:received', handleDmReceived);
    socket.on('party:created', handlePartyCreated);
    socket.on('party:joined', handlePartyJoined);
    socket.on('party:my_party_response', handleMyPartyResponse);
    socket.on('party:left', handlePartyLeft);
    socket.on('party:invitation', handlePartyInvitation);
    socket.on('party:member_joined', handlePartyMemberJoined);
    socket.on('party:member_left', handlePartyMemberLeft);
    socket.on('party:chat_message', handlePartyChatMessage);
    socket.on('party:list_response', handlePartyListResponse);
    socket.on('party:list_updated', handlePartyListUpdated);
    socket.on('party:game_started', handlePartyGameStarted);
    socket.on('party:kicked', handlePartyKicked);
    socket.on('party:leader_changed', handlePartyLeaderChanged);
    socket.on('party:error', handlePartyError);

    return () => {
      socket.off('connect', handleConnect);
      socket.off('friend:initial_data', handleInitialData);
      socket.off('friend:list_response', handleFriendListResponse);
      socket.off('friend:request_received', handleFriendRequestReceived);
      socket.off('friend:accepted', handleFriendAccepted);
      socket.off('friend:removed', handleFriendRemoved);
      socket.off('presence:update', handlePresenceUpdate);
      socket.off('dm:received', handleDmReceived);
      socket.off('party:created', handlePartyCreated);
      socket.off('party:joined', handlePartyJoined);
      socket.off('party:my_party_response', handleMyPartyResponse);
      socket.off('party:left', handlePartyLeft);
      socket.off('party:invitation', handlePartyInvitation);
      socket.off('party:member_joined', handlePartyMemberJoined);
      socket.off('party:member_left', handlePartyMemberLeft);
      socket.off('party:chat_message', handlePartyChatMessage);
      socket.off('party:list_response', handlePartyListResponse);
      socket.off('party:list_updated', handlePartyListUpdated);
      socket.off('party:game_started', handlePartyGameStarted);
      socket.off('party:kicked', handlePartyKicked);
      socket.off('party:leader_changed', handlePartyLeaderChanged);
      socket.off('party:error', handlePartyError);
    };
  }, [socket, setFriends, setFriendRequests]);

  // Send friend request
  const sendFriendRequest = useCallback(
    async (usernameOrId: string | number, message?: string) => {
      return new Promise<void>((resolve, reject) => {
        if (!socket) {
          reject(new Error('Not connected'));
          return;
        }

        // If it's a number, use it as targetUserId, otherwise use username
        const targetUserId = typeof usernameOrId === 'number' ? usernameOrId : null;
        const targetUsername = typeof usernameOrId === 'string' ? usernameOrId : null;

        socket.emit('friend:request', {
          targetUserId,
          targetUsername,
          message
        });

        socket.once('friend:request_response', (response: { success: boolean; error?: string }) => {
          if (response.success) {
            resolve();
          } else {
            reject(new Error(response.error || 'Failed to send friend request'));
          }
        });

        // Timeout after 5 seconds
        setTimeout(() => reject(new Error('Request timeout')), 5000);
      });
    },
    [socket]
  );

  // Accept friend request
  const acceptFriendRequest = useCallback(
    (requestId: number) => {
      if (!socket) {
        console.error('[useFriends] Cannot accept - socket not connected');
        return;
      }
      console.log('[useFriends] Accepting friend request:', requestId);
      socket.emit('friend:accept', { requestId });

      // Wait for response before removing from store
      socket.once('friend:accept_response', (response: { success: boolean; error?: string }) => {
        console.log('[useFriends] Accept response:', response);
        if (response.success) {
          removeRequestFromStore(requestId);
          if (typeof window !== 'undefined' && (window as any).toast) {
            (window as any).toast.success('Friend request accepted!');
          }
        } else {
          console.error('[useFriends] Failed to accept:', response.error);
          if (typeof window !== 'undefined' && (window as any).toast) {
            (window as any).toast.error(response.error || 'Failed to accept friend request');
          }
        }
      });
    },
    [socket, removeRequestFromStore]
  );

  // Decline friend request
  const declineFriendRequest = useCallback(
    (requestId: number) => {
      if (!socket) {
        console.error('[useFriends] Cannot decline - socket not connected');
        return;
      }
      console.log('[useFriends] Declining friend request:', requestId);
      socket.emit('friend:decline', { requestId });

      // Wait for response before removing from store
      socket.once('friend:decline_response', (response: { success: boolean; error?: string }) => {
        console.log('[useFriends] Decline response:', response);
        if (response.success) {
          removeRequestFromStore(requestId);
          if (typeof window !== 'undefined' && (window as any).toast) {
            (window as any).toast.info('Friend request declined');
          }
        } else {
          console.error('[useFriends] Failed to decline:', response.error);
          if (typeof window !== 'undefined' && (window as any).toast) {
            (window as any).toast.error(response.error || 'Failed to decline friend request');
          }
        }
      });
    },
    [socket, removeRequestFromStore]
  );

  // Remove friend
  const removeFriend = useCallback(
    (friendId: number) => {
      if (!socket) return;
      socket.emit('friend:remove', { friendUserId: friendId });
      removeFromStore(friendId);
    },
    [socket, removeFromStore]
  );

  // Send direct message
  const sendDirectMessage = useCallback(
    async (toUserId: string | number, message: string) => {
      return new Promise<void>((resolve, reject) => {
        if (!socket) {
          reject(new Error('Not connected'));
          return;
        }

        // Validate parameters
        if (!toUserId || !message || message.trim() === '') {
          reject(new Error('Invalid parameters: toUserId and message are required'));
          return;
        }

        let resolved = false;

        const handleResponse = (response: { success: boolean }) => {
          if (resolved) return;
          resolved = true;
          if (response.success) {
            resolve();
          } else {
            reject(new Error('Failed to send message'));
          }
        };

        const handleError = (error: { error: string }) => {
          if (resolved) return;
          resolved = true;
          reject(new Error(error.error || 'Failed to send message'));
        };

        const timeout = setTimeout(() => {
          if (resolved) return;
          resolved = true;
          socket.off('dm:sent', handleResponse);
          socket.off('dm:error', handleError);
          reject(new Error('Request timeout'));
        }, 10000); // Increased to 10 seconds

        socket.once('dm:sent', (response: { success: boolean }) => {
          clearTimeout(timeout);
          socket.off('dm:error', handleError);
          handleResponse(response);
        });

        socket.once('dm:error', (error: { error: string }) => {
          clearTimeout(timeout);
          socket.off('dm:sent', handleResponse);
          handleError(error);
        });

        socket.emit('dm:send', { toUserId, message });
      });
    },
    [socket]
  );

  // Get direct message history
  const getDirectMessages = useCallback(
    async (withUserId: string | number, limit = 50) => {
      return new Promise<any[]>((resolve, reject) => {
        if (!socket) {
          reject(new Error('Not connected'));
          return;
        }

        // Validate parameters
        if (!withUserId) {
          reject(new Error('Invalid parameters: withUserId is required'));
          return;
        }

        let resolved = false;

        const handleResponse = (response: { messages: any[] }) => {
          if (resolved) return;
          resolved = true;
          resolve(response.messages || []);
        };

        const handleError = (error: { error: string }) => {
          if (resolved) return;
          resolved = true;
          reject(new Error(error.error || 'Failed to load messages'));
        };

        const timeout = setTimeout(() => {
          if (resolved) return;
          resolved = true;
          socket.off('dm:history_response', handleResponse);
          socket.off('dm:error', handleError);
          reject(new Error('Request timeout'));
        }, 10000); // Increased to 10 seconds

        socket.once('dm:history_response', (response: { messages: any[] }) => {
          clearTimeout(timeout);
          socket.off('dm:error', handleError);
          handleResponse(response);
        });

        socket.once('dm:error', (error: { error: string }) => {
          clearTimeout(timeout);
          socket.off('dm:history_response', handleResponse);
          handleError(error);
        });

        socket.emit('dm:history', { withUserId, limit });
      });
    },
    [socket]
  );

  // Mark messages as read
  const markMessagesRead = useCallback(
    (fromUserId: string | number) => {
      if (!socket) return;

      // Validate parameters
      if (!fromUserId) {
        console.error('markMessagesRead: fromUserId is required');
        return;
      }

      socket.emit('dm:mark_read', { fromUserId });
    },
    [socket]
  );

  // Party functions
  const createParty = useCallback(() => {
    if (!socket) return;
    socket.emit('party:create', {});
  }, [socket]);

  const inviteToParty = useCallback(
    (targetUserId: number) => {
      if (!socket) return;
      socket.emit('party:invite', { targetUserId });
    },
    [socket]
  );

  const joinParty = useCallback(
    (partyId: string) => {
      if (!socket) return;
      console.log('[useFriends] Joining party:', partyId);
      socket.emit('party:join', { partyId });
    },
    [socket]
  );

  const leaveParty = useCallback(() => {
    if (!socket) return;
    console.log('[useFriends] Leaving party');
    socket.emit('party:leave');
    // Don't set party to null here - wait for party:left event
  }, [socket]);

  const sendPartyChat = useCallback(
    (message: string) => {
      if (!socket || !party) return;
      socket.emit('party:chat', { message });
      setParty((prev) =>
        prev
          ? {
              ...prev,
              messages: [
                ...prev.messages,
                { username: 'You', message, timestamp: new Date() },
              ],
            }
          : null
      );
    },
    [socket, party]
  );

  const refreshPartyList = useCallback(() => {
    if (!socket) return;
    console.log('[useFriends] Refreshing party list');
    socket.emit('party:list');
  }, [socket]);

  const kickMember = useCallback(
    (targetUserId: number) => {
      if (!socket) return;
      console.log('[useFriends] Kicking member:', targetUserId);
      socket.emit('party:kick', { targetUserId });
    },
    [socket]
  );

  const startGame = useCallback(
    (gameId: number, mode: string = 'RANKED', region: string = 'EU') => {
      if (!socket || !party) return;
      console.log('[useFriends] Starting party queue:', { gameId, mode, region, partyId: party.id });
      socket.emit('party:start_game', { gameId, mode, region });
    },
    [socket, party]
  );

  // Clear unread count for a specific friend (called when DM modal opens)
  const clearUnread = useCallback((friendId: string | number) => {
    setUnreadMessages(prev => {
      const next = { ...prev };
      delete next[String(friendId)];
      return next;
    });
  }, []);

  // Subscribe to real-time DM events (used by DirectMessageModal)
  const onDmReceived = useCallback((callback: (msg: any) => void) => {
    dmListeners.add(callback);
    return () => { dmListeners.delete(callback); };
  }, [dmListeners]);

  return {
    friends,
    friendRequests,
    onlineFriends,
    party,
    openParties,
    isConnected,
    unreadMessages,
    clearUnread,
    onDmReceived,
    sendFriendRequest,
    acceptFriendRequest,
    declineFriendRequest,
    removeFriend,
    sendDirectMessage,
    getDirectMessages,
    markMessagesRead,
    createParty,
    inviteToParty,
    joinParty,
    leaveParty,
    sendPartyChat,
    refreshPartyList,
    kickMember,
    startGame,
  };
}
